import {
  Box,
  Button,
  Grid,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Collapse,
  Chip,
  Paper,
  Tooltip,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useStyles } from "../styles/index.styles";
import { useNavigate } from "react-router-dom";
import AvatarHiplade from "../../common/components/avatarhiplade";
import StationsService from "../../services/StationsService";
import StreamingService from "../../services/StreamingService";
import colors from "../../common/styles/colors";
import palette from "../../common/styles/palette";
import loadstatus from "../../assets/images/load-status.gif";
import AttachmentService from "../../services/AttachmentService";
import DebriefingService from "../../services/DebriefingService";
import { toast } from "react-toastify";
import LoaderHiplade from "../../common/components/loaderhiplade";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddToDriveIcon from "@mui/icons-material/AddToDrive";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import StopIcon from "@mui/icons-material/Stop";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import DebriefingStatusWatcher from "./DebriefingStatusWatcher";
import secureLocalStorage from "react-secure-storage";
import IDebriefingData from "../../types/Debriefing";
import StreamingManagementService from "../../services/StreamingManagementService";
import DriveSingleImportModal from "./drive-import/DriveSingleImportModal";

// =============================================================================
// Constantes de domínio
// =============================================================================

/**
 * Status que colocam o debriefing em alguma fase de correção ou revisão.
 * Quando ativo, exibe o botão de acessar/corrigir para avaliadores.
 */
const STATUS_CORRECAO = [
  "not_corrected",
  "correcting",
  "corrected",
  "revised",
  "pending",
] as const;

/**
 * Status de falha durante a criação ou execução do streaming.
 * Quando ativo, permite reiniciar a simulação (se câmera disponível).
 */
const STATUS_ERRO = [
  "error_create_streaming_hiplade",
  "error_merdiaservice_create_streaming",
  "error_configuring_obs",
  "rtmp_connection_failed",
  "error_hiplade_transmition_method",
  "error_hiplade_useffmpeg",
  "error_agent",
  "error_rca",
  "error_gcp_create_streaming",
  "error_gcp_provision",
  "error_gcp_rca",
] as const;

// =============================================================================
// Classificação de situação do aluno para filtro rápido
// =============================================================================

export type SituacaoAluno =
  | "all"
  | "no_simulation"
  | "in_simulation"
  | "ready_to_correct"
  | "correcting"
  | "corrected";

export function getSituacaoAluno(student: any): SituacaoAluno {
  const status = student?.debriefing_status;
  if (status === "corrected" || status === "revised") {
    return "corrected";
  }
  if (status === "correcting") {
    return "correcting";
  }
  if (status === "running" || status === "in_simulation" || status === "initializing") {
    return "in_simulation";
  }
  const isVideoFailed =
    student?.debriefingVideoStatus === "failed" || status === "transcode_failed";
  if (status === "not_corrected" || (student?.has_debriefing_video_attachment && !isVideoFailed)) {
    return "ready_to_correct";
  }
  return "no_simulation";
}

export function getDebriefingStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case "not_corrected":
      return "Não corrigido";
    case "correcting":
      return "Em correção";
    case "corrected":
      return "Corrigido";
    case "revised":
      return "Revisado";
    case "running":
      return "Ao vivo";
    case "in_simulation":
      return "Em simulação";
    case "initializing":
      return "Inicializando";
    case "created_hiplade":
      return "Criado no Hiplade";
    case "processing_recording":
      return "Processando gravação";
    case "transcode_failed":
      return "Erro no vídeo";
    default:
      return "Não iniciado";
  }
}

// =============================================================================
// Tipo: ações disponíveis por aluno
// =============================================================================

/**
 * Conjunto de flags de visibilidade/permissão calculadas para cada aluno.
 *
 * PRINCÍPIO: toda regra de exibição vive exclusivamente em `getAcoesAluno()`.
 * O JSX abaixo apenas consome estas flags — nenhuma condição composta deve
 * ser escrita diretamente no template.
 *
 * Para adicionar, remover ou alterar qualquer regra de visibilidade,
 * edite apenas `getAcoesAluno()`.
 */
interface AcoesAluno {
  /** Botão "Iniciar Debriefing" — vídeo já anexado, sem status de correção */
  canStartDebriefingFromVideo: boolean;

  waitingDebriefingFromVideo: boolean;
  /** Upload em andamento (pending/processing): exibe "Enviando vídeo..." */
  uploadingVideo: boolean;
  /** Upload de vídeo MP4 (somente desktop; mobile usa o modal) */
  canUploadVideo: boolean;
  /** Iniciar simulação via câmera (somente desktop; mobile usa o modal) */
  canStartSimulation: boolean;
  /** Encerrar simulação em andamento */
  canStopSimulation: boolean;
  /** Assistir transmissão ao vivo durante simulação */
  canViewLive: boolean;
  /** Acessar ou corrigir debriefing em fase de correção/revisão */
  canAccessDebriefing: boolean;
  /** Exibe label textual com o status atual do debriefing */
  showStatusLabel: boolean;
  /** Exibe gif de carregamento (initializing / processing_recording) */
  showStatusLoader: boolean;
  /** Exibe DebriefingStatusWatcher (polling periódico do status) */
  showStatusWatcher: boolean;
  /** Desktop: exibe texto "Não iniciado" para role viewer (role 0) */
  showNotStartedDesktop: boolean;
  /** Modal mobile: exibe texto "Não iniciado" para avaliador externo (role 5) */
  showNotStartedModal: boolean;
  /** Mobile: exibe ícone de seta para abrir modal de opções */
  showMobileOptionsArrow: boolean;
  /** Modal mobile: exibe botão de iniciar simulação */
  canModalStartSimulation: boolean;
  /** Modal mobile: exibe botão de upload de vídeo */
  canModalUploadVideo: boolean;
}

// =============================================================================
// Função de permissões — fonte única de verdade
// =============================================================================

/**
 * Computa as ações disponíveis para um aluno em uma estação.
 *
 * É uma função pura: dado o mesmo contexto, sempre retorna o mesmo resultado.
 * Isso facilita testes unitários e garante que novas regras sejam adicionadas
 * em um único lugar, sem riscos de inconsistência no template.
 *
 * Mapeamento de roles:
 *  - 0 → aluno/espectador: apenas visualiza, sem ações de modificação
 *  - 1 → admin: acessa debriefings existentes, não cria novos
 *  - 2 → professor: igual ao admin
 *  - 3 → professor-avaliador: ações dependem de `isAuthInAppraisers`
 *  - 5 → avaliador externo: no desktop tem acesso, no mobile é bloqueado
 */
function getAcoesAluno(params: {
  debriefingStatus: string | null | undefined;
  debriefing_id: number | null | undefined;
  stationStatus: string;
  hasCamera: boolean;
  hasVideoAttachment: boolean;
  urlVideoAttachment?: string | null;
  /** pending | processing | done | failed | null — status do upload no GCS */
  debriefingVideoStatus?: string | null;
  inSimulation: boolean;
  simulationLoading: boolean;
  role: string | number | null | undefined | number | boolean | object;
  isAuthInAppraisers: boolean;
  isCurrentUser: boolean;
}): AcoesAluno {
  const {
    debriefingStatus,
    stationStatus,
    hasCamera,
    hasVideoAttachment,
    inSimulation,
    simulationLoading,
    role,
    isAuthInAppraisers,
    isCurrentUser,
    urlVideoAttachment,
    debriefingVideoStatus,
  } = params;

  // ── Derivações de role ─────────────────────────────────────────────────────

  const roleStr = String(role ?? "");

  /**
   * role 0 — apenas visualiza: não pode fazer upload, simulação ou encerrar.
   * Equivalente à flag `hideActionsForNotAuthorized` do componente original.
   */
  const isViewer = roleStr === "0";

  /**
   * role 5 — avaliador externo: vê a lista no desktop, mas no modal mobile
   * recebe a mensagem "Não iniciado" sem opções de ação.
   * Preserva o comportamento original (desktop não bloqueia role 5 explicitamente).
   */
  const isAppraiser = roleStr === "5";

  /**
   * role 3 sem vínculo com esta estação específica.
   * A função `startDebriefing` exibe um toast de erro para este caso.
   */
  const isTeacherNotAppraiser = roleStr === "3" && !isAuthInAppraisers;

  /** Bloqueia ações de modificação no modal mobile */
  const isBlockedInModal = isViewer || isAppraiser || isTeacherNotAppraiser;

  // ── Derivações de status ───────────────────────────────────────────────────

  const isConcluded       = stationStatus === "concluded";
  const isInSimulation    = debriefingStatus === "running";
  const isInitializing    = debriefingStatus === "initializing";
  const isProcessing      = debriefingStatus === "processing_recording" || debriefingStatus === "transcode_failed";
  const createdAtHiplade    = debriefingStatus === "created_hiplade";
  const hasCorrecaoStatus = STATUS_CORRECAO.includes(debriefingStatus as any);
  const hasErrorStatus    = STATUS_ERRO.includes(debriefingStatus as any);
  const noStatus          = !debriefingStatus || debriefingStatus === "finished";
  const debriefingId = params.debriefing_id;

  // Feedback em tempo real do upload (BHCBAI-tela): o status do attachment no
  // GCS distingue "ainda subindo" (pending/processing) de "pronto" (done) e de
  // "falhou" (failed). Status null = registros antigos pré-coluna status:
  // tratados como prontos quando existe URL.
  const isVideoUploading  = debriefingVideoStatus === "pending" || debriefingVideoStatus === "processing";

  // Se tem anexo de vídeo e URL gravada válida, o vídeo está pronto para debriefing
  // independentemente de debriefing_video_status (a presença da URL garante início do debriefing).
  const hasValidVideoUrl  = Boolean(
    hasVideoAttachment &&
    urlVideoAttachment &&
    urlVideoAttachment !== "null" &&
    String(urlVideoAttachment).trim() !== ""
  );

  // Falha real de vídeo: status failed e NÃO há URL válida utilizável
  const isVideoFailed     = debriefingVideoStatus === "failed" && !hasValidVideoUrl;

  // Vídeo pronto para debriefing: URL válida existente (não subindo) ou anexo prévio não falhado
  const isVideoReady      = hasValidVideoUrl
    ? !isVideoUploading
    : (hasVideoAttachment && !isVideoUploading && !isVideoFailed);

  return {
    /**
     * Vídeo já enviado aguardando início do debriefing.
     * Sem filtro de role — preserva comportamento original.
     */
    canStartDebriefingFromVideo: isVideoReady && noStatus && urlVideoAttachment != null && debriefingId == null,

    waitingDebriefingFromVideo: isVideoReady && urlVideoAttachment == null && !isConcluded,

    /** Upload ainda em andamento: substitui o botão por "Enviando vídeo..." */
    uploadingVideo: hasVideoAttachment && isVideoUploading,

    /**
     * Upload MP4 no desktop: estação aberta, sem status, sem vídeo prévio utilizável.
     * Upload que FALHOU sem URL utilizável reabre o botão: o registro órfão não bloqueia nova tentativa.
     * Somente role 0 é bloqueado.
     */
    canUploadVideo: !isConcluded && (hasErrorStatus || noStatus) && (!hasVideoAttachment || isVideoFailed) && !isViewer,

    /**
     * Iniciar simulação: câmera disponível, estação aberta, sem simulação ativa,
     * sem vídeo prévio utilizável, role com permissão (apenas role 0 bloqueado no desktop).
     */
    canStartSimulation:
      hasCamera &&
      !isConcluded &&
      (hasErrorStatus || noStatus) &&
      !inSimulation &&
      !simulationLoading &&
      (!hasVideoAttachment || isVideoFailed) &&
      !isViewer,

    /**
     * Encerrar simulação: apenas quando em simulação e role não é viewer.
     * No original, role 0 tinha `display: none`; aqui simplesmente não renderiza.
     */
    canStopSimulation: isInSimulation && !isViewer,

    /**
     * Live: disponível durante simulação se estação não está concluída.
     * Qualquer role pode assistir ao vivo — sem restrição.
     */
    canViewLive: isInSimulation && !isConcluded,

    /**
     * Acesso/correção do debriefing: ativo quando em fase de correção e não é o
     * próprio avaliado.
     *
     * CORREÇÃO V2: A verificação agora usa a chave "id" em ambas as telas (mobile e
     * desktop). O componente original usava "user_id" no mobile e "id" no desktop,
     * causando comportamento inconsistente para algumas combinações de usuário.
     */
    canAccessDebriefing: hasCorrecaoStatus && !isCurrentUser,

    // Labels e indicadores de progresso
    showStatusLabel:   true,
    showStatusLoader:  isInitializing || isProcessing,
    showStatusWatcher: isInitializing || isInSimulation || createdAtHiplade || isProcessing,

/** "Não iniciado" no desktop — somente para role 0 sem vídeo utilizável nem status */
    showNotStartedDesktop: isViewer && noStatus && (!hasVideoAttachment || isVideoFailed),

    /** "Não iniciado" no modal mobile — somente para role 5 (avaliador externo) */
    showNotStartedModal: isAppraiser,

    /** Seta mobile: exibe quando não há status, vídeo utilizável nem simulação ativa */
    showMobileOptionsArrow: !inSimulation && noStatus && (!hasVideoAttachment || isVideoFailed),

    // Modal mobile: role 5 e role 0 não podem iniciar ações
    canModalStartSimulation:
      hasCamera && !inSimulation && !simulationLoading && !isBlockedInModal,
    canModalUploadVideo: !isBlockedInModal,
  };
}

// =============================================================================
// Helpers visuais (fora do componente para reuso e testabilidade)
// =============================================================================

/**
 * Retorna a cor associada ao status do debriefing.
 * Mantém o mesmo mapeamento do componente original.
 */
function getStatusColor(status: string): string {
  if (status === "initializing" || status === "processing_recording")
    return colors.purpleStatus;
  if (status === "not_corrected" || status === "in_simulation")
    return colors.pinkLight;
  if (status === "correcting")
    return colors.yellow;
  if (STATUS_ERRO.includes(status as any) || status === "transcode_failed")
    return colors.redMedium;
  return colors.greenMedium; // corrected, revised e demais
}

/**
 * Retorna o ícone do botão de acesso/correção conforme o status.
 */
function getDebriefingIcon(status: string) {
  if (status === "corrected" || status === "revised") return <CheckCircleIcon />;
  if (status === "correcting")                        return <AccessTimeFilledIcon />;
  return <ErrorOutlinedIcon />;
}

/**
 * Retorna o label do botão de acesso/correção conforme o status.
 */
function getDebriefingLabel(status: string, t: (key: string) => string): string {
  if (status === "corrected" || status === "revised") return t("btn_access");
  if (status === "correcting")                        return t("in_progress");
  return t("bt_correct");
}

// =============================================================================
// Cache em memória compartilhado por estação e filtro de avaliador:
// chave: `${stationId}_${appraiserId || 'all'}`
// =============================================================================
const stationStudentsCache = new Map<
  string,
  { evaluateds: any[]; in_simulation: boolean; timestamp: number }
>();
const CACHE_TTL_MS = 30_000;

// =============================================================================
// Componente
// =============================================================================

export default function StationStudentsListV2({
  studentsgroupId,
  eventId,
  stationId,
  status_m,
  station,
  appraiserId,         // prop opcional para filtrar por avaliador
  isAuthInAppraisers = true, // indica se o usuário é avaliador nesta estação
  refreshTrigger,      // trigger opcional para forçar re-fetch externamente
  isLazy = false,      // se true, aguarda proximidade do viewport antes de buscar alunos
}: any) {
  // Guarda de entrada: sem grupo de alunos não há nada a renderizar
  if (!studentsgroupId) return <Box />;

  const [hasEnteredViewport, setHasEnteredViewport] = useState(!isLazy);
  const containerRef                                = useRef<HTMLDivElement | null>(null);

  const [status, setStatus]                         = useState(status_m);
  const { t }                                       = useTranslation();
  const styles                                      = useStyles();
  const navigate                                    = useNavigate();

  const [students, setStudents]                     = useState<any[]>([]);
  const [inSimulation, setInSimulation]             = useState(false);
  const [simulationLoading, setSimulationLoading]   = useState(false);
  const [selectedStudent, setSelectedStudent]       = useState<any>(null);
  const [openModal, setOpenModal]                   = useState(false);
  const [loader, setLoader]                         = useState(false);

  const [actionLoading, setActionLoading]           = useState(false);

  // Estados para o menu de upload e modal do Google Drive
  const [uploadMenuAnchor, setUploadMenuAnchor]     = useState<null | HTMLElement>(null);
  const [studentForUpload, setStudentForUpload]     = useState<any>(null);
  const [pendingLocalUploadStudent, setPendingLocalUploadStudent] = useState<any>(null);
  const [driveSingleModalOpen, setDriveSingleModalOpen] = useState(false);
  const [studentForDriveSingle, setStudentForDriveSingle] = useState<any>(null);
  const localFileInputRef                           = useRef<HTMLInputElement | null>(null);

  // Observer para carregar dados de estações fora do viewport apenas quando o usuário rolar próximo
  useEffect(() => {
    if (hasEnteredViewport) return;
    if (typeof IntersectionObserver === "undefined") {
      setHasEnteredViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setHasEnteredViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" } // dispara 300px antes de chegar no viewport
    );
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, [hasEnteredViewport, isLazy]);

  /**
   * Dados do usuário autenticado lidos uma única vez por render.
   * O projeto usa tanto "id" quanto "user_id" como chave — aqui normalizamos
   * sempre para "id", com "user_id" como fallback para compatibilidade.
   */
  const currRole      = secureLocalStorage.getItem("role");
  const currentUserId = Number(
    secureLocalStorage.getItem("id") ?? secureLocalStorage.getItem("user_id") ?? 0
  );

  // Filtro por situação e controle de detalhes expandidos
  const [situacaoFilter, setSituacaoFilter]         = useState<SituacaoAluno>("all");
  const [expandedStudentIds, setExpandedStudentIds] = useState<Set<number>>(new Set());

  const handleToggleDetails = (studentId: number) => {
    setExpandedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  };

  const countsPorSituacao = useMemo(() => {
    const counts: Record<SituacaoAluno, number> = {
      all: 0,
      no_simulation: 0,
      in_simulation: 0,
      ready_to_correct: 0,
      correcting: 0,
      corrected: 0,
    };
    students.forEach((s: any) => {
      if (s.user_id === currentUserId) return;
      counts.all += 1;
      const sit = getSituacaoAluno(s);
      if (counts[sit] !== undefined) {
        counts[sit] += 1;
      }
    });
    return counts;
  }, [students, currentUserId]);

  const filteredStudents = useMemo(() => {
    return students.filter((s: any) => {
      if (s.user_id === currentUserId) return false;
      if (situacaoFilter === "all") return true;
      return getSituacaoAluno(s) === situacaoFilter;
    });
  }, [students, situacaoFilter, currentUserId]);

  /**
   * Ações do aluno selecionado no modal mobile.
   * Recomputadas via useMemo apenas quando `selectedStudent` ou estado relevante mudam,
   * evitando reprocessamento desnecessário a cada render do componente pai.
   */
  const modalAcoes = useMemo<AcoesAluno | null>(
    () =>
      selectedStudent
        ? getAcoesAluno({
            debriefingStatus:   selectedStudent?.debriefing_status,
            debriefing_id:      selectedStudent?.debriefing_id,
            stationStatus:      status,
            hasCamera:          station.has_camera,
            hasVideoAttachment: selectedStudent?.has_debriefing_video_attachment,
            urlVideoAttachment: selectedStudent?.urlVideoAttachment,
            debriefingVideoStatus: selectedStudent?.debriefingVideoStatus,
            inSimulation,
            simulationLoading,
            role:               currRole,
            isAuthInAppraisers,
            isCurrentUser:      selectedStudent?.user_id === currentUserId,
          })
        : null,

    [selectedStudent, status, inSimulation, simulationLoading]
  );

  // ── Handlers ────────────────────────────────────────────────────────────────

  const getStudentsList = async (sid: any, silent = false, forceRefresh = false) => {
    // Avaliador externo sem autorização nesta estação: lista permanece vazia
    if (secureLocalStorage.getItem("role") == 5 && !isAuthInAppraisers) {
      setStudents([]);
      setInSimulation(false);
      return;
    }

    const cacheKey = `${sid}_${appraiserId || "all"}`;
    const cached = stationStudentsCache.get(cacheKey);
    const now = Date.now();

    // Se temos dados válidos em cache e não é refresh forçado, renderiza instantaneamente
    if (!forceRefresh && cached && now - cached.timestamp < CACHE_TTL_MS) {
      setStudents(cached.evaluateds);
      setInSimulation(cached.in_simulation);
      return;
    }

    try {
      if (!silent) setLoader(true);
      const response: any = await StationsService.getUsersStations(sid, appraiserId);
      if (response.data?.evaluateds?.length > 0) {
        // Normaliza os campos snake_case do backend (url_debriefing_video_attachment,
        // debriefing_video_status) para as chaves camelCase consumidas abaixo.
        const normalizados = response.data.evaluateds.map((s: any) => ({
          ...s,
          urlVideoAttachment: s.url_debriefing_video_attachment,
          debriefingVideoStatus: s.debriefing_video_status,
        }));
        setStudents(normalizados);
        setInSimulation(response.data.in_simulation);
        stationStudentsCache.set(cacheKey, {
          evaluateds: normalizados,
          in_simulation: response.data.in_simulation,
          timestamp: now,
        });
      } else {
        setStudents([]);
        setInSimulation(response.data?.in_simulation ?? false);
        stationStudentsCache.set(cacheKey, {
          evaluateds: [],
          in_simulation: response.data?.in_simulation ?? false,
          timestamp: now,
        });
      }
    } catch (_) {
    } finally {
      if (!silent) setLoader(false);
    }
  };

  const startDebriefing = async (
    studentId: any,
    evId: any,
    stId: any,
    debriefingId: any
  ) => {
    try {
      if (!currentUserId) {
        toast("Usuário não autenticado", { type: "error" });
        return;
      }

      // Role 3 sem vínculo com esta estação não pode criar avaliação
      if (String(currRole) === "3" && !isAuthInAppraisers) {
        toast("Usuário não autorizado para iniciar uma avaliação", { type: "info" });
        return;
      }

      // Admin (1) e professor (2): apenas acessam debriefings existentes, não criam novos
      if (currRole == 1 || currRole == 2) {
        if (debriefingId) {
          navigate(`/events/scenarios/view/${evId}/${stId}/${studentId}/${debriefingId}`);
        } else {
          toast("Acesso negado! Verifique se tem permissão para criar este debriefing.", { type: "error" });
        }
        return;
      }

      // Verifica se já existe um debriefing deste appraiser para o avaliado + estação
      const resp = await DebriefingService.getByStationIdEvaluatedId(studentId, stId);
      const debriefings = resp?.data?.debriefings ?? resp?.data ?? [];
      const existing = Array.isArray(debriefings)
        ? debriefings.find((d: any) => Number(d.appraiser_id) === currentUserId)
        : null;

      if (existing) {
        const idToOpen = existing.debriefing_id ?? existing.id;
        if (idToOpen) {
          navigate(`/events/scenarios/view/${evId}/${stId}/${studentId}/${idToOpen}`);
          return;
        }
      }

      // Cria novo debriefing e navega para a tela de avaliação
      const data: IDebriefingData = {
        evaluated_id: studentId,
        station_id:   stId,
        event_id:     evId,
      };
      const storeResp = await DebriefingService.store(data);
      const newId = storeResp["data"]["debriefing_id"] ?? storeResp["data"]["id"];

      if (newId) {
        navigate(`/events/scenarios/view/${evId}/${stId}/${studentId}/${newId}`);
      } else {
        toast("Erro ao criar debriefing", { type: "error" });
      }
    } catch (error: any) {
      toast(error?.response?.data?.Error ?? "Erro ao iniciar debriefing", { type: "error" });
    }
  };

  const startSimulation = async (evaluated_id: any) => {
    setSimulationLoading(true);
    setStudents(prev =>
          prev.map(s =>
            s.user_id === evaluated_id
              ? { ...s, debriefing_status: "initializing" }
              : s
          )
        );
    try {
      const data = {
        evaluated_id,
        station_id: stationId,
        event_id:   eventId,
        name:       `Streaming ${evaluated_id}${stationId}${eventId}`,
      };
      const response: any = await StreamingService.store(data);
      if (response.data) {
        // Atualização otimista: exibe loader de "initializing" imediatamente,
        // sem aguardar o refetch do servidor.
        //console.log(response.data.debriefing_status)
        if (response.data.debriefing_status !== undefined) {
          setStudents(prev =>
            prev.map(s =>
              s.user_id === evaluated_id
                ? { ...s, debriefing_status: response.data.debriefing_status || "initializing" }
                : s
            )
          );

        }
        setSimulationLoading(false);
        getStudentsList(stationId);
      }
    } catch (_) {
      // setSimulationLoading(false);
      // toast("Falha ao iniciar simulação, verifique as configurações e tente novamente.", { type: "error" });
    } finally {

    }
  };

  const stopSimulation = async (streaming_id: any) => {
    setActionLoading(true);
    try {
      const response: any = await StreamingManagementService.forceStop(streaming_id);
      if (response.data) {
        getStudentsList(stationId, false, true);
        setActionLoading(false);
        toast("Simulação encerrada com sucesso.", { type: "success" });
      }
    } catch (_) {
      setActionLoading(false);
    }
  };

  const uploadFile = async (event: any, evId: any, stId: any, userId: any) => {
    const data = {
      feature:    "debriefing",
      object_id:  `${evId}${stId}${userId}`,
      field_name: "debriefing_video",
      file:       event.target.files[0],
      file_type:  "video",
      multiple:   "false",
      /** Os dados abaixo são para identificação do video no Google Storage */
      event_id:   evId,
      station_id: stId,
      user_id:    userId,
    };
    setLoader(true);
    try {
      const response: any = await AttachmentService.upload(data);
      if (response.status === 200) {
        getStudentsList(stId, false, true);
        setLoader(false);
        toast(t("toast_load_video"), { type: "success" });
        window.location.reload();
      }
      if (response.data?.Error || response.status === 400) {
        toast(response.data.Msg, { type: "error" });
        setLoader(false);
        window.location.reload();
      }
    } catch (e: any) {
      setLoader(false);
      toast(e?.response?.data?.Error, { type: "error" });
    }
  };

  const handleOpenModal  = (student: any) => { setSelectedStudent(student); setOpenModal(true); };
  const handleCloseModal = ()             => { setOpenModal(false); setSelectedStudent(null); };

  const handleOpenUploadMenu = (event: React.MouseEvent<HTMLElement>, student: any) => {
    setUploadMenuAnchor(event.currentTarget);
    setStudentForUpload(student);
  };

  const handleCloseUploadMenu = () => {
    setUploadMenuAnchor(null);
    setStudentForUpload(null);
  };

  const handleChooseLocalFile = () => {
    const student = studentForUpload;
    handleCloseUploadMenu();
    if (student && localFileInputRef.current) {
      setPendingLocalUploadStudent(student);
      localFileInputRef.current.value = "";
      localFileInputRef.current.click();
    }
  };

  const handleChooseDriveImport = () => {
    const student = studentForUpload;
    handleCloseUploadMenu();
    if (student) {
      setStudentForDriveSingle(student);
      setDriveSingleModalOpen(true);
    }
  };

  const handleDriveSingleSuccess = () => {
    if (stationId) {
      getStudentsList(stationId, false, true);
    }
  };

  // Recarrega a lista sempre que o filtro de avaliador, autorização ou refreshTrigger mudam,
  // apenas quando a estação já entrou no viewport
  useEffect(() => {
    if (stationId && hasEnteredViewport) {
      const isForced = refreshTrigger !== undefined && refreshTrigger > 0;
      getStudentsList(stationId, false, isForced);
    }
  }, [studentsgroupId, eventId, stationId, appraiserId, isAuthInAppraisers, refreshTrigger, hasEnteredViewport]);

  // Feedback em tempo real do upload: enquanto algum aluno da estação tem
  // vídeo em pending/processing (job de upload rodando), reconsulta a lista a
  // cada 5s para a tela trocar "Enviando vídeo..." por "Iniciar Debriefing"
  // assim que o attachment virar done — sem reload manual.
  const hasUploadInFlight = students.some(
    (s: any) => s.debriefingVideoStatus === "pending" || s.debriefingVideoStatus === "processing"
  );
  useEffect(() => {
    if (!stationId || !hasUploadInFlight || !hasEnteredViewport) return;
    const timer = setInterval(() => getStudentsList(stationId, true, true), 5000);
    return () => clearInterval(timer);
  }, [stationId, hasUploadInFlight, appraiserId, isAuthInAppraisers, hasEnteredViewport]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!hasEnteredViewport) {
    return (
      <Box
        ref={containerRef}
        sx={{
          minHeight: 70,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        <CircularProgress size={18} sx={{ color: palette.brand }} />
        <Typography variant="body2" sx={{ color: palette.textMuted }}>
          Carregando alunos da estação...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {loader && <LoaderHiplade />}

      {/* ── Barra de Filtros por Situação ──────────────────────────────────── */}
      {students.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: palette.textMuted,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              mr: 0.5,
            }}
          >
            Filtrar:
          </Typography>

          <Chip
            size="small"
            label={`Todos (${countsPorSituacao.all})`}
            onClick={() => setSituacaoFilter("all")}
            sx={{
              cursor: "pointer",
              fontWeight: situacaoFilter === "all" ? 700 : 500,
              bgcolor: situacaoFilter === "all" ? palette.brand : palette.muted,
              color: situacaoFilter === "all" ? "#fff" : palette.text,
              "&:hover": {
                bgcolor: situacaoFilter === "all" ? palette.brandDark : palette.border,
              },
            }}
          />

          <Chip
            size="small"
            label={`Sem simulação (${countsPorSituacao.no_simulation})`}
            onClick={() => setSituacaoFilter("no_simulation")}
            sx={{
              cursor: "pointer",
              fontWeight: situacaoFilter === "no_simulation" ? 700 : 500,
              bgcolor: situacaoFilter === "no_simulation" ? palette.brand : palette.muted,
              color: situacaoFilter === "no_simulation" ? "#fff" : palette.text,
              "&:hover": {
                bgcolor: situacaoFilter === "no_simulation" ? palette.brandDark : palette.border,
              },
            }}
          />

          {countsPorSituacao.in_simulation > 0 && (
            <Chip
              size="small"
              label={`Em simulação (${countsPorSituacao.in_simulation})`}
              onClick={() => setSituacaoFilter("in_simulation")}
              sx={{
                cursor: "pointer",
                fontWeight: situacaoFilter === "in_simulation" ? 700 : 500,
                bgcolor: situacaoFilter === "in_simulation" ? palette.brand : palette.muted,
                color: situacaoFilter === "in_simulation" ? "#fff" : palette.text,
                "&:hover": {
                  bgcolor: situacaoFilter === "in_simulation" ? palette.brandDark : palette.border,
                },
              }}
            />
          )}

          <Chip
            size="small"
            label={`Pronto pra corrigir (${countsPorSituacao.ready_to_correct})`}
            onClick={() => setSituacaoFilter("ready_to_correct")}
            sx={{
              cursor: "pointer",
              fontWeight: situacaoFilter === "ready_to_correct" ? 700 : 500,
              bgcolor: situacaoFilter === "ready_to_correct" ? palette.brand : palette.muted,
              color: situacaoFilter === "ready_to_correct" ? "#fff" : palette.text,
              "&:hover": {
                bgcolor: situacaoFilter === "ready_to_correct" ? palette.brandDark : palette.border,
              },
            }}
          />

          <Chip
            size="small"
            label={`Em correção (${countsPorSituacao.correcting})`}
            onClick={() => setSituacaoFilter("correcting")}
            sx={{
              cursor: "pointer",
              fontWeight: situacaoFilter === "correcting" ? 700 : 500,
              bgcolor: situacaoFilter === "correcting" ? palette.brand : palette.muted,
              color: situacaoFilter === "correcting" ? "#fff" : palette.text,
              "&:hover": {
                bgcolor: situacaoFilter === "correcting" ? palette.brandDark : palette.border,
              },
            }}
          />

          <Chip
            size="small"
            label={`Corrigido (${countsPorSituacao.corrected})`}
            onClick={() => setSituacaoFilter("corrected")}
            sx={{
              cursor: "pointer",
              fontWeight: situacaoFilter === "corrected" ? 700 : 500,
              bgcolor: situacaoFilter === "corrected" ? palette.brand : palette.muted,
              color: situacaoFilter === "corrected" ? "#fff" : palette.text,
              "&:hover": {
                bgcolor: situacaoFilter === "corrected" ? palette.brandDark : palette.border,
              },
            }}
          />
        </Box>
      )}

      {students.length > 0 && filteredStudents.length === 0 && (
        <Typography
          variant="body2"
          sx={{ py: 3, textAlign: "center", color: palette.textMuted }}
        >
          Nenhum aluno encontrado para este filtro.
        </Typography>
      )}

      {filteredStudents.map((student: any, index: number) => {
        // Oculta sempre a linha do próprio usuário autenticado
        if (student.user_id === currentUserId) return null;

        const isExpanded = expandedStudentIds.has(student.user_id);

        /**
         * Objeto de ações deste aluno — única fonte de verdade para toda a
         * lógica de visibilidade dentro do bloco abaixo.
         *
         * Para depurar o comportamento de um aluno específico, inspecione
         * este objeto: ele reflete exatamente o que será (ou não) renderizado.
         */
        const acoes = getAcoesAluno({
          debriefingStatus:   student?.debriefing_status,
          debriefing_id:      student?.debriefing_id,
          stationStatus:      status,
          hasCamera:          station.has_camera,
          hasVideoAttachment: student?.has_debriefing_video_attachment,
          urlVideoAttachment: student?.urlVideoAttachment,
          debriefingVideoStatus: student?.debriefingVideoStatus,
          inSimulation,
          simulationLoading,
          role:               currRole,
          isAuthInAppraisers,
          isCurrentUser:      student?.user_id === currentUserId,
        });

        return (
          <Box key={student.user_id || index} sx={{ mb: 1.5 }}>
            <Grid
              container
              spacing={1}
              className={styles.eventStationListStudents}
              sx={{
                mb: 0,
                borderBottomLeftRadius: isExpanded ? 0 : palette.radius,
                borderBottomRightRadius: isExpanded ? 0 : palette.radius,
                borderBottom: isExpanded ? `1px dashed ${colors.grayDLight}` : undefined,
                transition: "border-radius 0.2s ease",
              }}
            >
              {/* ── Coluna "+ Detalhes" / "- Detalhes" — visível apenas em lg+ ── */}
              <Grid
                item
                xs={2}
                sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center" }}
              >
                <Button
                  size="small"
                  onClick={() => handleToggleDetails(student.user_id)}
                  endIcon={
                    isExpanded ? (
                      <KeyboardArrowUpIcon sx={{ fontSize: "18px !important" }} />
                    ) : (
                      <KeyboardArrowDownIcon sx={{ fontSize: "18px !important" }} />
                    )
                  }
                  sx={{
                    color: isExpanded ? colors.purpleDark : colors.purpleDetail,
                    fontSize: "0.875rem !important",
                    fontWeight: 600,
                    textTransform: "none",
                    p: 0,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: colors.purpleDark,
                    },
                  }}
                >
                  {isExpanded ? "- Detalhes" : "+ Detalhes"}
                </Button>
              </Grid>

            {/* ── Coluna Avatar + Nome ─────────────────────────────────────── */}
            {/*
             * Breakpoints nativos MUI substituem as variáveis isLargeScreen/isMediumScreen.
             * xs=4 → mobile (sem coluna de detalhes, 4 de 12)
             * sm=8 → tablet (sem coluna de detalhes, 8 de 12)
             * lg=6 → desktop (com coluna de detalhes de 2, totaliza 12)
             */}
            <Grid
              item
              xs={4}
              sm={8}
              lg={6}
              className={styles.boxContentAvatar}
              onClick={acoes.showMobileOptionsArrow ? () => handleOpenModal(student) : undefined}
              sx={acoes.showMobileOptionsArrow ? { cursor: "pointer" } : {}}
            >
              <AvatarHiplade
                nameUser={student?.user_name}
                size="mini"
                theme="gray"
                id={student?.user_id}
                onlyInitials
              />
              <Typography className={styles.titleStudent}>
                {/* Prefixo "Aluno (a)/" — oculto em xs, visível em sm+ */}
                <Box
                  component="span"
                  sx={{ display: { xs: "none", sm: "inline" }, fontWeight: "bold" }}
                >
                  Aluno (a)/{" "}
                </Box>
                {student?.user_name}
              </Typography>
            </Grid>

            {/* ── Coluna de Ações ──────────────────────────────────────────── */}
            {/*
             * xs=8 → mobile  | sm=4 → tablet/desktop
             * Cada ação abaixo renderiza ícone (mobile) e/ou botão (desktop)
             * usando `display: { xs, sm }` em vez de renderização condicional por JS.
             */}
            <Grid
              item
              xs={8}
              sm={4}
              lg={4}
              className={styles.alignButtonsListStation}
            >

              {/* ── Label do status atual (desktop) ── */}
              {acoes.showStatusLabel && (
                <Typography
                  sx={{
                    display: { xs: "none", sm: "block" },
                    fontWeight: 700,
                    color: getStatusColor(student?.debriefing_status),
                    fontSize: { xs: "12px", lg: "inherit" },
                  }}
                >
                  {student?.debriefing_status == "finished" ? "" : t(student?.debriefing_status)}
                </Typography>
              )}

              {/* ── Iniciar Debriefing (vídeo já anexado) ── */}
              {acoes.canStartDebriefingFromVideo && (
                <Button
                  variant="contained"
                  size="large"
                  color="secondary"
                  sx={{ ml: { xs: 0.5, sm: 2 }, fontSize: { xs: "12px", lg: "inherit" } }}
                  onClick={() =>
                    startDebriefing(
                      student.user_id,
                      eventId,
                      stationId,
                      student?.debriefing_id
                    )
                  }
                >
                  {t("btn_start_debriefing")}
                </Button>
              )}

              {/* ── Upload em andamento / aguardando vídeo ── */}
              {acoes.uploadingVideo && (
                <Typography
                  sx={{
                    ml: { xs: 0.5, sm: 2 },
                    fontSize: { xs: "12px", lg: "inherit" },
                    fontWeight: "bold",
                    color: colors.yellow,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {t("Enviando vídeo...")}
                </Typography>
              )}
              {acoes.waitingDebriefingFromVideo && acoes.canAccessDebriefing == false && (
                <span>
                  {t("Carregando vídeo...")}
                </span>
              )}

              {/* ── Upload de vídeo + "Não iniciado" (desktop apenas) ── */}
              {/*
               * O bloco inteiro fica oculto em mobile (xs: none, sm: block).
               * No mobile, o upload fica disponível dentro do modal.
               */}
              {(acoes.canUploadVideo || acoes.showNotStartedDesktop) && (
                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                  {acoes.canUploadVideo && (
                    <Box>
                      <Button
                        variant="outlined"
                        size="medium"
                        endIcon={<ArrowDropDownIcon />}
                        onClick={(e) => handleOpenUploadMenu(e, student)}
                        sx={{
                          fontWeight: "bold",
                          borderRadius: "4px",
                          color: colors.pinkLight,
                          border: `2px solid ${colors.pinkLight}`,
                          textTransform: "uppercase",
                          px: { xs: "5px", sm: "10px" },
                          py: "6px",
                          textAlign: "center",
                          width: { sm: "170px !important" },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: { xs: "11px", lg: "inherit" },
                          "&:hover": {
                            border: `2px solid ${colors.pinkLight}`,
                            bgcolor: "rgba(237, 50, 75, 0.04)",
                          },
                        }}
                      >
                        {t("btn_upload_video")}
                      </Button>
                    </Box>
                  )}

                  {/* Role viewer (0): sem ações, exibe apenas texto informativo */}
                  {acoes.showNotStartedDesktop && (
                    <Typography
                      sx={{ fontSize: { xs: "12px", lg: "inherit" }, textAlign: "center" }}
                    >
                      Não iniciado
                    </Typography>
                  )}
                </Box>
              )}

              {/* ── Gif de carregamento (initializing / processing_recording) ── */}
              {acoes.showStatusLoader && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                 //minHeight="100vh"
                >
                  <img src={loadstatus} style={{ marginLeft: 15, position: 'relative', top: 8 }} alt="Processando..." />
                  <Typography
                    align="center"
                    sx={{
                      display: { xs: "none", sm: "block" },
                      fontWeight: 700,
                      color: getStatusColor(student?.debriefing_status),
                      fontSize: { xs: "12px", lg: "inherit" },
                    }}
                  >
                    {student?.debriefing_status === "initializing"
                      ? t("initializing")
                      : t("processing_recording")}
                  </Typography>
                </Box>

              )}

              {/* ── Live: ícone (mobile) + botão (desktop) ── */}
              {acoes.canViewLive && (
                <>
                  <IconButton
                    sx={{
                      display: { xs: "flex", sm: "none" },
                      ml: 0.5,
                      color: getStatusColor(student?.debriefing_status),
                    }}
                    onClick={() =>
                      navigate(
                        `/events/live/${eventId}/${stationId}/${student?.user_id}/${student?.debriefing_id}`
                      )
                    }
                    aria-label={String(t("bt_live"))}
                  >
                    <LiveTvIcon />
                  </IconButton>
                  <Button
                    variant="outlined"
                    color="error"
                    sx={{
                      display: { xs: "none", sm: "flex" },
                      ml: { xs: 0.5, sm: 2 },
                      fontSize: { xs: "12px", lg: "inherit" },
                      padding: { xs: "6px 8px", lg: "6px 16px" },
                    }}
                    onClick={() =>
                      navigate(
                        `/events/live/${eventId}/${stationId}/${student?.user_id}/${eventId}${stationId}${student?.user_id}`
                      )
                    }
                  >
                    {t("bt_live")}
                  </Button>
                </>
              )}

              {/* ── Encerrar simulação: ícone (mobile) + botão (desktop) ── */}
              {acoes.canStopSimulation && (
                <>
                  <IconButton
                    sx={{
                      display: { xs: "flex", sm: "none" },
                      ml: 0.5,
                      color: getStatusColor(student?.debriefing_status),
                    }}
                    onClick={() => stopSimulation(student?.streaming_id)}
                    aria-label={String(t("bt_finish"))}
                  >
                    <StopIcon />
                  </IconButton>
                  <Button
                    variant="contained"
                    size="large"
                    color="secondary"
                    startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : ''}
                    disabled={actionLoading}
                    sx={{
                      display: { xs: "none", sm: "flex" },
                      ml: { xs: 0.5, sm: 2 },
                      fontSize: { xs: "12px", lg: "inherit" },
                    }}
                    onClick={() => stopSimulation(student?.streaming_id)}
                  >
                    {t("bt_finish")}
                  </Button>
                </>
              )}

              {/* ── Acesso/Correção do debriefing: ícone (mobile) + botão (desktop) ── */}
              {acoes.canAccessDebriefing && (
                <>
                  <IconButton
                    sx={{
                      display: { xs: "flex", sm: "none" },
                      ml: 0.5,
                      color: getStatusColor(student?.debriefing_status),
                    }}
                    onClick={() =>
                      startDebriefing(
                        student.user_id,
                        eventId,
                        stationId,
                        student?.debriefing_id
                      )
                    }
                    aria-label={String(getDebriefingLabel(student?.debriefing_status, t))}
                  >
                    {getDebriefingIcon(student?.debriefing_status)}
                  </IconButton>
                  <Button
                    variant="contained"
                    size="large"
                    color="secondary"
                    sx={{
                      display: { xs: "none", sm: "flex" },
                      ml: { xs: 0.5, sm: 2 },
                      fontSize: { xs: "12px", lg: "inherit" },
                    }}
                    onClick={() =>
                      startDebriefing(
                        student.user_id,
                        eventId,
                        stationId,
                        student?.debriefing_id
                      )
                    }
                  >
                    {getDebriefingLabel(student?.debriefing_status, t)}
                  </Button>
                </>
              )}

              {/* ── Iniciar Simulação (desktop apenas) ── */}
              {acoes.canStartSimulation && (
                <Button
                  variant="outlined"
                  size="large"
                  className={styles.buttonStartSimulation}
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    ml: { xs: 0.5, sm: 2 },
                    fontSize: { xs: "12px", lg: "inherit" },
                  }}
                  onClick={() => startSimulation(student?.user_id)}
                >
                  {t("btn_start_simulation")}
                </Button>
              )}

              {/* ── Seta mobile: abre modal de opções ── */}
              {acoes.showMobileOptionsArrow && (
                <IconButton
                  sx={{ display: { xs: "flex", sm: "none" }, color: colors.purple }}
                  onClick={() => handleOpenModal(student)}
                  aria-label={String(t("btn_options"))}
                >
                  <ArrowForwardOutlinedIcon />
                </IconButton>
              )}
            </Grid>

            {/* ── DebriefingStatusWatcher (polling periódico de status) ── */}
            {acoes.showStatusWatcher && (
              <Grid item xs={12} sx={{ mt: 1 }}>
                <DebriefingStatusWatcher
                  status={student?.debriefing_status}
                  debriefingId={`${eventId}${stationId}${student?.user_id}`}
                  onStatusChange={(newStatus: any) => {
                    setSimulationLoading(false);
                    // Atualiza somente o aluno afetado, preservando o restante da lista
                    setStudents((prev) =>
                      prev.map((s) =>
                        s.user_id === student.user_id
                          ? { ...s, debriefing_status: newStatus }
                          : s
                      )
                    );
                  }}
                />
              </Grid>
            )}
          </Grid>

          {/* ── Painel de Detalhes Expansível (Collapse) ────────────────────── */}
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: "#F8FAFC",
                border: `1px solid ${colors.grayDLight}`,
                borderTop: "none",
                borderBottomLeftRadius: palette.radius,
                borderBottomRightRadius: palette.radius,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                {/* Seção 1: Dados do Aluno */}
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: palette.textMuted,
                      display: "block",
                      mb: 0.8,
                    }}
                  >
                    Dados do Aluno
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: palette.text }}>
                      {student?.user_name || "Não informado"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: palette.textMuted, wordBreak: "break-all" }}>
                      E-mail: {student?.user_email || student?.email || "Não informado"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: palette.textMuted }}>
                      ID do Aluno: {student?.user_id}
                    </Typography>
                  </Box>
                </Grid>

                <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" }, mx: 0.5 }} />

                {/* Seção 2: Situação da Estação & Status do Vídeo */}
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: palette.textMuted,
                      display: "block",
                      mb: 0.8,
                    }}
                  >
                    Situação da Estação & Vídeo
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 1 }}>
                    <Chip
                      size="small"
                      label={`Avaliação: ${getDebriefingStatusLabel(student?.debriefing_status)}`}
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        bgcolor:
                          student?.debriefing_status === "corrected" || student?.debriefing_status === "revised"
                            ? "#E8F5E9"
                            : student?.debriefing_status === "correcting"
                            ? "#FFF3E0"
                            : student?.debriefing_status === "not_corrected"
                            ? "#E3F2FD"
                            : "#F1F5F9",
                        color:
                          student?.debriefing_status === "corrected" || student?.debriefing_status === "revised"
                            ? colors.greenDark
                            : student?.debriefing_status === "correcting"
                            ? colors.yellow
                            : student?.debriefing_status === "not_corrected"
                            ? colors.blueMedium
                            : palette.textMuted,
                      }}
                    />

                    {student?.has_debriefing_video_attachment ? (
                      <Chip
                        size="small"
                        label={
                          student?.debriefingVideoStatus === "pending" || student?.debriefingVideoStatus === "processing"
                            ? "Processando vídeo"
                            : student?.debriefingVideoStatus === "failed"
                            ? "Falha no vídeo"
                            : "Vídeo anexado"
                        }
                        sx={{
                          fontWeight: 600,
                          fontSize: "12px",
                          bgcolor:
                            student?.debriefingVideoStatus === "failed"
                              ? "#FFEBEE"
                              : student?.debriefingVideoStatus === "pending" || student?.debriefingVideoStatus === "processing"
                              ? "#FFF8E1"
                              : "#E8F5E9",
                          color:
                            student?.debriefingVideoStatus === "failed"
                              ? palette.danger
                              : student?.debriefingVideoStatus === "pending" || student?.debriefingVideoStatus === "processing"
                              ? colors.yellow
                              : colors.greenDark,
                        }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label="Sem vídeo anexado"
                        sx={{
                          fontWeight: 500,
                          fontSize: "12px",
                          bgcolor: "#F1F5F9",
                          color: palette.textMuted,
                        }}
                      />
                    )}
                  </Box>

                  {student?.urlVideoAttachment && (
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<PlayCircleOutlineIcon />}
                      onClick={() => window.open(student.urlVideoAttachment, "_blank", "noopener,noreferrer")}
                      sx={{
                        p: 0,
                        minWidth: 0,
                        color: palette.brand,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "13px",
                        "&:hover": { bgcolor: "transparent", color: palette.brandDark, textDecoration: "underline" },
                      }}
                    >
                      Assistir vídeo da simulação
                    </Button>
                  )}
                </Grid>

                <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" }, mx: 0.5 }} />

                {/* Seção 3: Ação de Atualizar / Anexar Vídeo */}
                <Grid item xs={12} md={3} sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: palette.textMuted,
                      display: "block",
                    }}
                  >
                    Gerenciar Vídeo
                  </Typography>

                  {acoes.canModalUploadVideo ? (
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<ArrowDropDownIcon />}
                      onClick={(e) => handleOpenUploadMenu(e, student)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: student?.has_debriefing_video_attachment ? palette.brand : colors.pinkLight,
                        borderColor: student?.has_debriefing_video_attachment ? palette.brand : colors.pinkLight,
                        "&:hover": {
                          borderColor: student?.has_debriefing_video_attachment ? palette.brandDark : colors.pinkLight,
                          bgcolor: student?.has_debriefing_video_attachment ? "rgba(99, 102, 241, 0.04)" : "rgba(237, 50, 75, 0.04)",
                        },
                      }}
                    >
                      {student?.has_debriefing_video_attachment ? "Atualizar Vídeo" : "Anexar Vídeo"}
                    </Button>
                  ) : (
                    <Tooltip title="Upload ou alteração de vídeo não disponível para esta estação ou perfil" arrow>
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "13px",
                          }}
                        >
                          {student?.has_debriefing_video_attachment ? "Atualizar Vídeo" : "Anexar Vídeo"}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Collapse>
        </Box>
      );
    })}

      {/* ── Modal de opções (mobile) ─────────────────────────────────────────── */}
      {/*
       * Exibido ao tocar no avatar/nome ou na seta em telas pequenas.
       * As permissões são calculadas via `modalAcoes` (useMemo) com base
       * no `selectedStudent`, usando a mesma função `getAcoesAluno`.
       */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="xs">
        {selectedStudent && modalAcoes && (
          <>
            <DialogContent>
              {/* Informações cadastrais e situação do aluno (Mobile) */}
              <Box sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${colors.grayDLight}` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <AvatarHiplade
                    nameUser={selectedStudent?.user_name}
                    size="mini"
                    theme="gray"
                    id={selectedStudent?.user_id}
                    onlyInitials
                  />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {selectedStudent?.user_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: palette.textMuted, display: "block" }}>
                      {selectedStudent?.user_email || selectedStudent?.email || "Sem e-mail"} • ID: {selectedStudent?.user_id}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, alignItems: "center", mt: 1 }}>
                  <Chip
                    size="small"
                    label={`Avaliação: ${getDebriefingStatusLabel(selectedStudent?.debriefing_status)}`}
                    sx={{
                      fontWeight: 600,
                      fontSize: "11px",
                      bgcolor:
                        selectedStudent?.debriefing_status === "corrected" || selectedStudent?.debriefing_status === "revised"
                          ? "#E8F5E9"
                          : selectedStudent?.debriefing_status === "correcting"
                          ? "#FFF3E0"
                          : selectedStudent?.debriefing_status === "not_corrected"
                          ? "#E3F2FD"
                          : "#F1F5F9",
                      color:
                        selectedStudent?.debriefing_status === "corrected" || selectedStudent?.debriefing_status === "revised"
                          ? colors.greenDark
                          : selectedStudent?.debriefing_status === "correcting"
                          ? colors.yellow
                          : selectedStudent?.debriefing_status === "not_corrected"
                          ? colors.blueMedium
                          : palette.textMuted,
                    }}
                  />
                  {selectedStudent?.has_debriefing_video_attachment ? (
                    <Chip
                      size="small"
                      label={
                        selectedStudent?.debriefingVideoStatus === "pending" || selectedStudent?.debriefingVideoStatus === "processing"
                          ? "Processando vídeo"
                          : selectedStudent?.debriefingVideoStatus === "failed"
                          ? "Falha no vídeo"
                          : "Vídeo anexado"
                      }
                      sx={{
                        fontWeight: 600,
                        fontSize: "11px",
                        bgcolor:
                          selectedStudent?.debriefingVideoStatus === "failed"
                            ? "#FFEBEE"
                            : selectedStudent?.debriefingVideoStatus === "pending" || selectedStudent?.debriefingVideoStatus === "processing"
                            ? "#FFF8E1"
                            : "#E8F5E9",
                        color:
                          selectedStudent?.debriefingVideoStatus === "failed"
                            ? palette.danger
                            : selectedStudent?.debriefingVideoStatus === "pending" || selectedStudent?.debriefingVideoStatus === "processing"
                            ? colors.yellow
                            : colors.greenDark,
                      }}
                    />
                  ) : (
                    <Chip
                      size="small"
                      label="Sem vídeo anexado"
                      sx={{
                        fontWeight: 500,
                        fontSize: "11px",
                        bgcolor: "#F1F5F9",
                        color: palette.textMuted,
                      }}
                    />
                  )}
                </Box>

                {selectedStudent?.urlVideoAttachment && (
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="text"
                      fullWidth
                      startIcon={<PlayCircleOutlineIcon />}
                      onClick={() => {
                        window.open(selectedStudent.urlVideoAttachment, "_blank", "noopener,noreferrer");
                        handleCloseModal();
                      }}
                      sx={{
                        color: palette.brand,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "13px",
                        justifyContent: "flex-start",
                        p: 0,
                      }}
                    >
                      Assistir vídeo da simulação
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Role 5 (avaliador externo) não pode iniciar ações no mobile */}
              {modalAcoes.showNotStartedModal ? (
                <Typography sx={{ mt: 2, fontSize: "12px", textAlign: "center" }}>
                  Não iniciado
                </Typography>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, fontSize: { xs: "14px", lg: "16px" } }}
                  >
                    {selectedStudent?.has_debriefing_video_attachment
                      ? "Opções de vídeo da estação:"
                      : "Para iniciar a estação escolha uma das opções abaixo:"}
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Iniciar simulação */}
                    {modalAcoes.canModalStartSimulation && (
                      <Grid item xs={6}>
                        <Button
                          variant="outlined"
                          fullWidth
                          sx={{
                            color: colors.greenMedium,
                            borderColor: colors.greenMedium,
                          }}
                          onClick={() => {
                            startSimulation(selectedStudent?.user_id);
                            handleCloseModal();
                          }}
                        >
                          {t("btn_start_simulation")}
                        </Button>
                      </Grid>
                    )}

                    {/* Upload de vídeo — Opções Local e Google Drive */}
                    {modalAcoes.canModalUploadVideo && (
                      <>
                        <Grid item xs={12}>
                          <input
                            id="modal-upload"
                            name="modal-upload"
                            style={{ display: "none" }}
                            accept="application/mp4"
                            type="file"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              if (e.target.files && e.target.files[0]) {
                                uploadFile(e, eventId, stationId, selectedStudent?.user_id);
                                handleCloseModal();
                              }
                            }}
                          />
                          <label htmlFor="modal-upload" style={{ width: "100%" }}>
                            <Button
                              component="span"
                              variant="outlined"
                              fullWidth
                              startIcon={<UploadFileIcon />}
                              sx={{ color: colors.pinkLight, borderColor: colors.pinkLight }}
                            >
                              Enviar MP4 do aparelho
                            </Button>
                          </label>
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<AddToDriveIcon />}
                            sx={{
                              color: palette.brand,
                              borderColor: palette.brand,
                              "&:hover": { borderColor: palette.brandDark },
                            }}
                            onClick={() => {
                              setStudentForDriveSingle(selectedStudent);
                              setDriveSingleModalOpen(true);
                              handleCloseModal();
                            }}
                          >
                            Importar do Google Drive
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal} sx={{ textTransform: "none", color: palette.textMuted }}>
                Fechar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Input de arquivo invisível acionado pelo menu desktop */}
      <input
        ref={localFileInputRef}
        type="file"
        accept="application/mp4"
        style={{ display: "none" }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files[0] && pendingLocalUploadStudent) {
            uploadFile(e, eventId, stationId, pendingLocalUploadStudent.user_id);
          }
        }}
      />

      {/* Menu suspenso de seleção do método de envio de vídeo */}
      <Menu
        anchorEl={uploadMenuAnchor}
        open={Boolean(uploadMenuAnchor)}
        onClose={handleCloseUploadMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MenuItem onClick={handleChooseLocalFile}>
          <ListItemIcon>
            <UploadFileIcon fontSize="small" sx={{ color: colors.pinkLight }} />
          </ListItemIcon>
          <ListItemText>Enviar arquivo MP4 (do computador)</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleChooseDriveImport}>
          <ListItemIcon>
            <AddToDriveIcon fontSize="small" sx={{ color: palette.brand }} />
          </ListItemIcon>
          <ListItemText>Importar do Google Drive</ListItemText>
        </MenuItem>
      </Menu>

      {/* Modal de importação individual do Google Drive */}
      {studentForDriveSingle && (
        <DriveSingleImportModal
          open={driveSingleModalOpen}
          onClose={() => {
            setDriveSingleModalOpen(false);
            setStudentForDriveSingle(null);
          }}
          onSuccess={handleDriveSingleSuccess}
          student={studentForDriveSingle}
          eventId={eventId}
          stationId={stationId}
        />
      )}
    </Box>
  );
}
